import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import SetPasswordPage from './components/SetPasswordPage';
import DashboardWithBackend from './components/DashboardWithBackend';
import PluginDownloadPage from './components/PluginDownloadPage';

// AI Features
import AIScriptMatcher from './components/AIScriptMatcher';

// Admin Components
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminCollectionManager from './components/admin/AdminCollectionManager';
import AdminAddScenes from './components/admin/AdminAddScenes';
import AdminEditScene from './components/admin/AdminEditScene';
import AdminUsers from './components/admin/AdminUsers';

// ✅ Admin Sounds
import AdminSounds from './components/admin/AdminSounds';
import AdminAddSound from './components/admin/AdminAddSound'; // ✅ use este (múltiplos + 422 fix)

// ✅ Admin SFX
import AdminAddSFX from './components/admin/AdminAddSFX';

// ✅ Admin Overlays
import AdminAddOverlays from './components/admin/AdminAddOverlays';

import { api } from "./services/api";

/**
 * Componente que protege rotas que requerem login de usuário
 */
const ProtectedRoute = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Componente que protege rotas admin
 */
const ProtectedAdminRoute = ({ children, isAdminAuthenticated, onLogout }) => {
  if (!isAdminAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  return React.cloneElement(children, { onAdminLogout: onLogout });
};

function App() {
  // Estado do usuário (autenticação real)
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  // Estado do admin
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      // Verificar token de usuário
      if (api.hasUserToken()) {
        try {
          const userData = await api.getMe();
          setUser(userData);
        } catch (error) {
          console.log('Token de usuário inválido, fazendo logout');
          api.logout();
        }
      }
      setUserLoading(false);

      // Verificar token admin
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        const isValid = await api.verifyAdminToken();
        setIsAdminAuthenticated(isValid);
        if (!isValid) {
          api.adminLogout();
        }
      }
      setAdminLoading(false);
    };

    restoreSession();
  }, []);

  const handleLogin = async (userData) => {
    setUser(userData.user);
  };

  const handleLogout = () => {
    api.logout();
    setUser(null);
  };

  const handleAdminLogin = () => {
    setIsAdminAuthenticated(true);
  };

  const handleAdminLogout = () => {
    api.adminLogout();
    setIsAdminAuthenticated(false);
  };

  if (userLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/library" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />

          <Route path="/set-password" element={<SetPasswordPage />} />

          <Route
            path="/library"
            element={
              <ProtectedRoute user={user} loading={userLoading}>
                <DashboardWithBackend user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* AI Script Matcher - Rota protegida por autenticação */}
          <Route
            path="/ai-matcher"
            element={
              <ProtectedRoute user={user} loading={userLoading}>
                <AIScriptMatcher />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/login"
            element={
              isAdminAuthenticated ? (
                <Navigate to="/admin" replace />
              ) : (
                <AdminLogin onLogin={handleAdminLogin} />
              )
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
                <AdminDashboard />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
                <AdminUsers />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/collection/:collectionName"
            element={
              <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
                <AdminCollectionManager />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/scenes/new"
            element={
              <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
                <AdminAddScenes />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/admin/scene/:sceneId/edit"
            element={
              <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
                <AdminEditScene />
              </ProtectedAdminRoute>
            }
          />

          {/* ✅ Admin: Músicas Virais */}
{/* A rota específica 'new' deve vir ANTES da rota geral */}
<Route
  path="/admin/sounds/new"
  element={
    <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
      <AdminAddSound />
    </ProtectedAdminRoute>
  }
/>

<Route
  path="/admin/sounds"
  element={
    <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
      <AdminSounds />
    </ProtectedAdminRoute>
  }
/>

<Route
  path="/admin/sfx/add"
  element={
    <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
      <AdminAddSFX />
    </ProtectedAdminRoute>
  }
/>

<Route
  path="/admin/overlays/add"
  element={
    <ProtectedAdminRoute isAdminAuthenticated={isAdminAuthenticated} onLogout={handleAdminLogout}>
      <AdminAddOverlays />
    </ProtectedAdminRoute>
  }
/>

<Route path="/plugin-download" element={<PluginDownloadPage />} />

<Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
